import * as PIXI from 'pixi.js';
import { ProcessSpectrum, GetSpectrumMagnitude } from '../../ProcessSpectrum';
import { CONSTANTS, PARTICLE_DIRECTIONS } from '../../PreviewDisplayConstants';
import { createMediaTexture, clearAllTextures, getDefaultTextures } from './textureUtils';
import { DEFAULT_PARTICLES_FLICKERING_SETTINGS, DEFAULT_FLOCK_SETTINGS, DEFAULT_PARTICLES_ROTATION_SETTINGS, PARTICLE_COUNT_MULTIPLIER, MAX_TOTAL_PARTICLE_COUNT } from '../../../CreatePageConstants';

const baseHeight = 500;
const baseSize = 50;


const separationRatio = 0.4;
const minSpeedRatio = 0.4;
const wrapMargin = 40;
const speedUpFactor = 100;
const flickerSpeedUpFactor = 100;
const maxBoids = MAX_TOTAL_PARTICLE_COUNT;
const spawnPerFrame = 20;
const rotationSmoothing = 0.15;

export default class Flock {
    constructor(stage, zIndex) {
        this.stage = stage;

        const defaultTextures = getDefaultTextures();

        this.defaultCircleTexture1 = defaultTextures.circleTexture1;
        this.defaultCircleTexture2 = defaultTextures.circleTexture2;
        this.defaultHeartTexture1 = defaultTextures.heartTexture1;
        this.defaultHeartTexture2 = defaultTextures.heartTexture2;
        this.defaultStarTexture1 = defaultTextures.starTexture1;
        this.defaultStarTexture2 = defaultTextures.starTexture2;
        this.defaultNoteTexture1 = defaultTextures.noteTexture1;
        this.defaultNoteTexture2 = defaultTextures.noteTexture2;

        this.particles = [];
        this.particleTextures = [];

        this.container = new PIXI.Container();
        this.stage.addChild(this.container);

        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;

        this.calculatedWidth = (baseHeight * CONSTANTS.DEFAULT_WIDTH) / CONSTANTS.DEFAULT_HEIGHT;
        this.calculatedHeight = (baseHeight * CONSTANTS.DEFAULT_HEIGHT) / CONSTANTS.DEFAULT_HEIGHT;
        this.x = CONSTANTS.DEFAULT_WIDTH * 0.5;
        this.y = CONSTANTS.DEFAULT_HEIGHT * 0.5;
        this.container.x = this.x;
        this.container.y = this.y;
        this.container.scale.set(multiplier, multiplier);
        this.container.zIndex = zIndex;

        this.direction = PARTICLE_DIRECTIONS.FLOCK;
        this.applyFlockSettings(DEFAULT_FLOCK_SETTINGS);
    }

    applyFlockSettings(flock) {
        this.separationWeight = flock.separation;
        this.alignmentWeight = flock.alignment;
        this.cohesionWeight = flock.cohesion;
        this.perceptionRadius = flock.vision;
        this.separationRadius = flock.vision * separationRatio;
        this.maxSpeed = flock.speed;
        this.minSpeed = flock.speed * minSpeedRatio;
        this.maxSteerForce = flock.speed * flock.turnSpeed;
    }

    update(config) {
        if (this.enabled !== config.enabled) {
            this.container.alpha = config.enabled ? 1 : 0;
            if (!config.enabled) {
                this.particles.forEach(p => p.sprite.destroy());
                this.container.removeChildren();
                this.particles = [];
            }
        }

        this.enabled = config.enabled;
        this.spectrumOptions = config.spectrumOptions;
        this.speedUpEnabled = config.speedUpEnabled;
        this.direction = config.direction;
        // Stored flock overrides only apply when the user turned on "Custom";
        // otherwise the flock always runs on the defaults.
        const flockConfig = config.flock?.customMode
            ? { ...DEFAULT_FLOCK_SETTINGS, ...config.flock }
            : DEFAULT_FLOCK_SETTINGS;
        this.applyFlockSettings(flockConfig);

        const newParticleTextures = config.particleTextures || [];
        const texturesChanged = JSON.stringify(this.particleTextures || []) !== JSON.stringify(newParticleTextures);

        if (texturesChanged) {
            this.particles.forEach(p => p.sprite.destroy());
            this.container.removeChildren();
            this.particles = [];
        }

        this.particleTextures = newParticleTextures;
        this.activeParticles = this.particleTextures.filter(config => (config.birthRate || 0) > 0);
    }

    draw(spectrum) {
        if (!this.enabled) return;

        const processedSpectrum = ProcessSpectrum(spectrum, this.spectrumOptions);
        const spectrumMagnitude = GetSpectrumMagnitude(processedSpectrum);
        const magnitudeRatio = spectrumMagnitude / 255;

        this.syncPopulation();

        if (this.particles.length === 0) return;

        // Reactive Speed: scale the displacement (not the steering forces — large
        // multipliers would make the steering integration unstable) so particles
        // shoot forward during loud audio.
        const speedMultiplier = this.speedUpEnabled ? 1 + magnitudeRatio * speedUpFactor : 1;
        const grid = this.buildSpatialGrid();

        const halfWidth = this.calculatedWidth * 0.5;
        const halfHeight = this.calculatedHeight * 0.5;

        // Pass 1: compute steering for every boid from the same snapshot of the
        // flock, so early movers don't skew the neighbours seen by later boids.
        this.particles.forEach((boid) => {
            const steer = this.computeFlockingSteer(boid, grid);
            boid.steerX = steer.x;
            boid.steerY = steer.y;
        });

        // Pass 2: integrate and render.
        this.particles.forEach((boid) => {
            boid.vx += boid.steerX;
            boid.vy += boid.steerY;

            this.limitSpeed(boid);

            boid.x += boid.vx * speedMultiplier;
            boid.y += boid.vy * speedMultiplier;

            const wrapX = halfWidth + wrapMargin;
            const wrapY = halfHeight + wrapMargin;
            if (boid.x > wrapX) boid.x = -wrapX;
            else if (boid.x < -wrapX) boid.x = wrapX;
            if (boid.y > wrapY) boid.y = -wrapY;
            else if (boid.y < -wrapY) boid.y = wrapY;

            const sprite = boid.sprite;
            sprite.x = boid.x;
            sprite.y = boid.y;

            if (boid.flickeringEnabled) {
                const flickerSpeedMultiplier = boid.reactive ? (1 + magnitudeRatio * flickerSpeedUpFactor) : 1;
                boid.opacityPhase += boid.opacitySpeed * flickerSpeedMultiplier;
                const normalizedSin = (Math.sin(boid.opacityPhase) + 1) / 2;
                sprite.alpha = boid.minOpacity + (boid.maxOpacity - boid.minOpacity) * normalizedSin;
            }

            if (boid.rotationEnabled) {
                // In the FLOCK direction, Rotation points each particle the way it is
                // heading (toward its velocity vector) instead of spinning it in place.
                // Textures are authored facing up, so offset by 90° to turn that up
                // edge into the direction of travel.
                const targetRotation = Math.atan2(boid.vy, boid.vx) + Math.PI / 2;
                const delta = Math.atan2(Math.sin(targetRotation - sprite.rotation), Math.cos(targetRotation - sprite.rotation));
                sprite.rotation += delta * rotationSmoothing;
            }
        });
    }

    computeFlockingSteer(boid, grid) {
        let sepX = 0, sepY = 0;
        let aliX = 0, aliY = 0;
        let cohX = 0, cohY = 0;
        let neighbourCount = 0;
        let separationCount = 0;

        const cellSize = this.perceptionRadius;
        const cellX = Math.floor(boid.x / cellSize);
        const cellY = Math.floor(boid.y / cellSize);

        for (let gx = cellX - 1; gx <= cellX + 1; gx++) {
            for (let gy = cellY - 1; gy <= cellY + 1; gy++) {
                const cell = grid.get(`${gx},${gy}`);
                if (!cell) continue;

                for (let i = 0; i < cell.length; i++) {
                    const other = cell[i];
                    if (other === boid) continue;

                    const dx = other.x - boid.x;
                    const dy = other.y - boid.y;
                    const distSq = dx * dx + dy * dy;

                    if (distSq > this.perceptionRadius * this.perceptionRadius || distSq === 0) continue;

                    // Alignment: match neighbours' heading.
                    aliX += other.vx;
                    aliY += other.vy;
                    // Cohesion: move toward neighbours' average position.
                    cohX += other.x;
                    cohY += other.y;
                    neighbourCount++;

                    // Separation: push away from anyone too close
                    if (distSq < this.separationRadius * this.separationRadius) {
                        sepX -= dx / distSq;
                        sepY -= dy / distSq;
                        separationCount++;
                    }
                }
            }
        }

        let steerX = 0, steerY = 0;

        if (neighbourCount > 0) {
            // Alignment: match neighbours' average heading.
            const alignment = this.steerToward(boid, aliX / neighbourCount, aliY / neighbourCount);
            steerX += alignment.x * this.alignmentWeight;
            steerY += alignment.y * this.alignmentWeight;

            // Cohesion: head toward neighbours' average position.
            const cohesion = this.steerToward(boid, cohX / neighbourCount - boid.x, cohY / neighbourCount - boid.y);
            steerX += cohesion.x * this.cohesionWeight;
            steerY += cohesion.y * this.cohesionWeight;
        }

        if (separationCount > 0) {
            const separation = this.steerToward(boid, sepX, sepY);
            steerX += separation.x * this.separationWeight;
            steerY += separation.y * this.separationWeight;
        }

        return { x: steerX, y: steerY };
    }

    steerToward(boid, desiredX, desiredY) {
        const desiredMag = Math.sqrt(desiredX * desiredX + desiredY * desiredY);
        if (desiredMag === 0) return { x: 0, y: 0 };

        const scale = this.maxSpeed / desiredMag;
        let forceX = desiredX * scale - boid.vx;
        let forceY = desiredY * scale - boid.vy;

        const forceMag = Math.sqrt(forceX * forceX + forceY * forceY);
        if (forceMag > this.maxSteerForce) {
            forceX = (forceX / forceMag) * this.maxSteerForce;
            forceY = (forceY / forceMag) * this.maxSteerForce;
        }

        return { x: forceX, y: forceY };
    }

    limitSpeed(boid) {
        const speed = Math.sqrt(boid.vx * boid.vx + boid.vy * boid.vy);
        if (speed > this.maxSpeed) {
            boid.vx = (boid.vx / speed) * this.maxSpeed;
            boid.vy = (boid.vy / speed) * this.maxSpeed;
        } else if (speed < this.minSpeed && speed > 0) {
            boid.vx = (boid.vx / speed) * this.minSpeed;
            boid.vy = (boid.vy / speed) * this.minSpeed;
        }
    }

    buildSpatialGrid() {
        const grid = new Map();
        const cellSize = this.perceptionRadius;

        this.particles.forEach((boid) => {
            const key = `${Math.floor(boid.x / cellSize)},${Math.floor(boid.y / cellSize)}`;
            let cell = grid.get(key);
            if (!cell) {
                cell = [];
                grid.set(key, cell);
            }
            cell.push(boid);
        });

        return grid;
    }

    syncPopulation() {
        if (!this.activeParticles || this.activeParticles.length === 0) {
            if (this.particles.length > 0) {
                this.particles.forEach(p => p.sprite.destroy());
                this.container.removeChildren();
                this.particles = [];
            }
            return;
        }

        const totalBirthRate = this.activeParticles.reduce((sum, config) => sum + (config.birthRate || 35), 0);
        const target = Math.min(maxBoids, Math.max(0, Math.round(totalBirthRate * PARTICLE_COUNT_MULTIPLIER)));

        if (this.particles.length < target) {
            const toAdd = Math.min(spawnPerFrame, target - this.particles.length);
            for (let i = 0; i < toAdd; i++) {
                const particleConfig = this.pickWeightedConfig(totalBirthRate);
                const boid = this.createParticle(this.parseConfig(particleConfig));
                this.particles.push(boid);
                this.container.addChild(boid.sprite);
            }
        } else if (this.particles.length > target) {
            const toRemove = this.particles.length - target;
            for (let i = 0; i < toRemove; i++) {
                const boid = this.particles.pop();
                boid.sprite.destroy();
            }
        }
    }

    pickWeightedConfig(totalBirthRate) {
        let roll = Math.random() * totalBirthRate;
        for (let i = 0; i < this.activeParticles.length; i++) {
            roll -= this.activeParticles[i].birthRate || 35;
            if (roll <= 0) return this.activeParticles[i];
        }
        return this.activeParticles[this.activeParticles.length - 1];
    }

    parseConfig(particleConfig) {
        return {
            mediaData: particleConfig?.mediaData,
            shape: particleConfig?.shape,
            tint: particleConfig?.color ? parseInt(particleConfig.color) : 0xffffff,
            minOpacity: particleConfig?.minOpacity ?? 0.2,
            maxOpacity: particleConfig?.maxOpacity ?? 1,
            minSize: particleConfig?.minSize ?? 0.1,
            maxSize: particleConfig?.maxSize ?? 0.3,
            flickeringEnabled: particleConfig?.flickering?.enabled ?? DEFAULT_PARTICLES_FLICKERING_SETTINGS.enabled,
            minFlickeringIntensity: particleConfig?.flickering?.minIntensity ?? DEFAULT_PARTICLES_FLICKERING_SETTINGS.minIntensity,
            maxFlickeringIntensity: particleConfig?.flickering?.maxIntensity ?? DEFAULT_PARTICLES_FLICKERING_SETTINGS.maxIntensity,
            reactive: particleConfig?.flickering?.reactive ?? DEFAULT_PARTICLES_FLICKERING_SETTINGS.reactive,
            rotationEnabled: particleConfig?.rotationEnabled ?? DEFAULT_PARTICLES_ROTATION_SETTINGS.enabled,
        };
    }

    createParticle(parsedConfig) {
        let texture;

        if (parsedConfig.mediaData) {
            texture = createMediaTexture(parsedConfig.shape, parsedConfig.mediaData);
        } else if (parsedConfig.shape === 'heart') {
            texture = Math.random() < 0.5 ? this.defaultHeartTexture1 : this.defaultHeartTexture2;
        } else if (parsedConfig.shape === 'star') {
            texture = Math.random() < 0.5 ? this.defaultStarTexture1 : this.defaultStarTexture2;
        } else if (parsedConfig.shape === 'note') {
            texture = Math.random() < 0.5 ? this.defaultNoteTexture1 : this.defaultNoteTexture2;
        } else {
            texture = Math.random() < 0.5 ? this.defaultCircleTexture1 : this.defaultCircleTexture2;
        }

        const sprite = new PIXI.Sprite(texture);

        sprite.tint = parsedConfig.tint;
        sprite.anchor.set(0.5);
        sprite.alpha = this.randomValue(parsedConfig.minOpacity, parsedConfig.maxOpacity);
        sprite.width = this.randomValue(baseSize * parsedConfig.minSize, baseSize * parsedConfig.maxSize);
        sprite.height = sprite.width;

        const { x, y, angle } = this.randomEdgeSpawn();
        const speed = this.randomValue(this.minSpeed, this.maxSpeed);

        // When Rotation is on, start already aligned to the heading (angle + 90° because
        // textures face up) so the smoothed rotation in draw() has nothing to swing from
        // on spawn; otherwise keep a fixed, unrotated orientation.
        sprite.rotation = parsedConfig.rotationEnabled ? angle + Math.PI / 2 : 0;

        sprite.x = x;
        sprite.y = y;

        return {
            sprite: sprite,
            x: x,
            y: y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            size: sprite.width,
            flickeringEnabled: parsedConfig.flickeringEnabled,
            reactive: parsedConfig.reactive,
            minOpacity: parsedConfig.minOpacity,
            maxOpacity: parsedConfig.maxOpacity,
            opacityPhase: Math.random() * Math.PI * 2,
            opacitySpeed: this.randomValue(parsedConfig.minFlickeringIntensity, parsedConfig.maxFlickeringIntensity),
            rotationEnabled: parsedConfig.rotationEnabled,
        };
    }

    // Fly-in spawn: new boids start just beyond a random frame edge (inside the
    // wrap zone), heading inward, so they stream into view exactly like regular
    // wrap crossings instead of popping up mid-frame.
    randomEdgeSpawn() {
        const halfWidth = this.calculatedWidth * 0.5;
        const halfHeight = this.calculatedHeight * 0.5;
        const offset = Math.random() * wrapMargin;
        const spread = (Math.random() - 0.5) * Math.PI * 0.5; // up to ±45° around the inward normal

        if (Math.random() * (this.calculatedWidth + this.calculatedHeight) < this.calculatedWidth) {
            const fromTop = Math.random() < 0.5;
            return {
                x: this.randomValue(halfWidth, 0, true),
                y: fromTop ? -halfHeight - offset : halfHeight + offset,
                angle: (fromTop ? Math.PI * 0.5 : -Math.PI * 0.5) + spread,
            };
        }

        const fromLeft = Math.random() < 0.5;
        return {
            x: fromLeft ? -halfWidth - offset : halfWidth + offset,
            y: this.randomValue(halfHeight, 0, true),
            angle: (fromLeft ? 0 : Math.PI) + spread,
        };
    }

    randomValue(min, max, negative = false) {
        let result = Math.random() * (max - min) + min;
        if (negative) {
            result = Math.random() < 0.5 ? result : -result;
        }
        return result;
    }

    updateStage(stage) {
        this.stage = stage;
        this.stage.addChild(this.container);

        this.calculatedWidth = (baseHeight * CONSTANTS.DEFAULT_WIDTH) / CONSTANTS.DEFAULT_HEIGHT;
        this.calculatedHeight = (baseHeight * CONSTANTS.DEFAULT_HEIGHT) / CONSTANTS.DEFAULT_HEIGHT;
    }

    destroy() {
        this.container.destroy({ children: true });
        this.particles = [];
        clearAllTextures();
    }
}
